import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Milestone } from '../types/Milestone';
import { authFetch } from '../lib/authFetch';

type Filters = { goalId?: string; status?: string; type?: string; };
type MilestonePatchKey =
  | 'goalId'
  | 'name'
  | 'description'
  | 'type'
  | 'targetAmount'
  | 'currentAmount'
  | 'checklist'
  | 'deadline'
  | 'linkedCategory'
  | 'linkedTransactionIds'
  | 'autoProgress'
  | 'dependencies'
  | 'status'
  | 'percentComplete'
  | 'tags'
  | 'isArchived';

const PATCHABLE_FIELDS = new Set<MilestonePatchKey>([
  'goalId',
  'name',
  'description',
  'type',
  'targetAmount',
  'currentAmount',
  'checklist',
  'deadline',
  'linkedCategory',
  'linkedTransactionIds',
  'autoProgress',
  'dependencies',
  'status',
  'percentComplete',
  'tags',
  'isArchived',
]);

const parseApiError = async (res: Response, fallback: string) => {
  try {
    const json = await res.json();
    if (json && typeof json.message === 'string' && json.message.trim()) return json.message;
  } catch {
    // no-op
  }
  return fallback;
};

const qs = (obj: Record<string, any>) => {
  const p = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => { if (v != null && v !== '') p.append(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const useMilestones = (refreshKey = 0, filters: Filters = {}) => {
  const { auth } = useAuth();
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reqRef = useRef(0);
  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    let cancelled = false; const id = ++reqRef.current;
    const fetchItems = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(`/api/milestones${qs(filters)}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (!res.ok) {
          const message = await parseApiError(res, 'Failed to load milestones');
          if (!cancelled && id === reqRef.current) {
            setMilestones([]);
            setError(message);
          }
          return;
        }
        const json = await res.json(); // { data: Milestone[] }
        if (!cancelled && id === reqRef.current) setMilestones(json.data || []);
      } catch (e) {
        if (!cancelled && id === reqRef.current) {
          setMilestones([]);
          setError('Failed to load milestones');
        }
      } finally {
        if (!cancelled && id === reqRef.current) setLoading(false);
      }
    };
    fetchItems();
    return () => { cancelled = true; };
  }, [auth.token, refreshKey, filtersKey, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const createMilestone = async (payload: Partial<Milestone>) => {
    setError(null);
    const res = await authFetch('/api/milestones', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const message = await parseApiError(res, 'Failed to create milestone');
      setError(message);
      throw new Error(message);
    }
    const json = await res.json();
    setMilestones(prev => [json.data, ...prev]);
    return json.data as Milestone;
  };

  const updateMilestone = async (id: string, payload: Partial<Milestone>) => {
    setError(null);
    if (Object.prototype.hasOwnProperty.call(payload, 'userId')) {
      const message = 'userId is not writable';
      setError(message);
      throw new Error(message);
    }
    const sanitizedPayload: Partial<Milestone> = {};
    for (const [key, value] of Object.entries(payload)) {
      if (!PATCHABLE_FIELDS.has(key as MilestonePatchKey)) {
        const message = `Unknown key: ${key}`;
        setError(message);
        throw new Error(message);
      }
      (sanitizedPayload as Record<string, unknown>)[key] = value;
    }

    const res = await authFetch(`/api/milestones/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
      body: JSON.stringify(sanitizedPayload),
    });
    if (!res.ok) {
      const message = await parseApiError(res, 'Failed to update milestone');
      setError(message);
      throw new Error(message);
    }
    const json = await res.json();
    setMilestones(prev => prev.map(m => (m._id === id ? json.data : m)));
    return json.data as Milestone;
  };

  const completeMilestone = async (id: string) => {
    setError(null);
    // optimistic
    setMilestones(prev => prev.map(m => (m._id === id ? { ...m, status: 'completed', percentComplete: 100 } : m)));
    const res = await authFetch(`/api/milestones/${id}/complete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth.token}` },
    });
    if (!res.ok) {
      const message = await parseApiError(res, 'Failed to complete milestone');
      setError(message);
      reload();
      throw new Error(message);
    }
    const json = await res.json();
    setMilestones(prev => prev.map(m => (m._id === id ? json.data : m)));
    return json.data as Milestone;
  };

  const recalc = async (id: string) => {
    setError(null);
    const res = await authFetch(`/api/milestones/${id}/recalc`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth.token}` },
    });
    if (!res.ok) {
      const message = await parseApiError(res, 'Failed to recalculate milestone');
      setError(message);
      throw new Error(message);
    }
    const json = await res.json();
    setMilestones(prev => prev.map(m => (m._id === id ? json.data : m)));
    return json.data as Milestone;
  };

  return { milestones, loading, error, reload, createMilestone, updateMilestone, completeMilestone, recalc, setMilestones };
};
